/**
 * Custom Predictive Search with API Integration
 *
 * Overrides Shopify's default predictive search to use custom API
 * Falls back to Shopify's predictive search if API fails
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://private.api.searchaiengine.com/shopify/search-track',
    timeout: 3000, // 3 seconds for predictive (faster than full search)
    testMode: false,
    debounceDelay: 300 // milliseconds
  };

  // Wait for the custom element to be defined
  if (customElements.get('predictive-search')) {
    console.log('[CustomPredictiveSearch] Overriding Shopify predictive search');

    // Store original getSearchResults method
    const OriginalPredictiveSearch = customElements.get('predictive-search');
    const originalGetSearchResults = OriginalPredictiveSearch.prototype.getSearchResults;

    // Override the getSearchResults method
    OriginalPredictiveSearch.prototype.getSearchResults = async function(searchTerm) {
      console.log('[CustomPredictiveSearch] Searching for:', searchTerm);

      try {
        // Call custom API
        const apiResults = await callCustomAPI(searchTerm);

        if (apiResults && apiResults.performed && apiResults.results) {
          console.log('[CustomPredictiveSearch] ✓ API Success! Got', apiResults.resultsCount, 'results');

          // Render custom results
          renderPredictiveResults(this.predictiveSearchResults, apiResults);
          this.open();
        } else {
          throw new Error('Invalid API response');
        }
      } catch (error) {
        console.log('[CustomPredictiveSearch] ✗ API Failed:', error.message);
        console.log('[CustomPredictiveSearch] >>> Falling back to Shopify predictive search');

        // Fallback to original Shopify method
        originalGetSearchResults.call(this, searchTerm);
      }
    };
  }

  /**
   * Call custom API for predictive search
   */
  function callCustomAPI(query) {
    if (CONFIG.testMode) {
      console.log('[CustomPredictiveSearch] 🧪 TEST MODE - Using static data');
      return getTestData(query);
    }

    console.log('[CustomPredictiveSearch] Calling API...');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);

    return fetch(CONFIG.apiEndpoint, {
      method: 'POST',
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': '1'
      },
      body: JSON.stringify({
        query: query,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        language: Shopify.locale || 'en',
        currency: Shopify.country || 'US',
        sortBy: 'relevance',
        filters: {},
        limit: 3 // Limit for predictive search
      }),
      signal: controller.signal
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .catch(error => {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('API timeout');
        }
        throw error;
      });
  }

  /**
   * Render predictive search results (matching Shopify's exact HTML structure)
   */
  function renderPredictiveResults(container, apiResults) {
    if (!container) return;

    const products = apiResults.results || [];
    const searchTerms = apiResults.terms || '';

    // Match Shopify's exact structure
    let html = '';

    if (products.length === 0) {
      html = `
        <div class="predictive-search-results">
          <div class="predictive-search__no-results">
            <p>No results found for "${escapeHtml(searchTerms)}"</p>
          </div>
        </div>
      `;
    } else {
      html = `
        <div class="predictive-search-results" id="predictive-search-results">
          <div class="results__group-1" style="display: none"></div>
          <div class="results__group-2">
            <div class="results results--products">
              <h3 class="h4" id="predictive-search-products">
                Products
                <span class="ai-badge">Smarter Results with AI Search</span>
              </h3>
              <ul role="listbox" aria-labelledby="predictive-search-products">
      `;
      //"Search with AI on light gray"
      /*
      html = `
        <div class="predictive-search-results" id="predictive-search-results">
          <div class="results__group-1" style="display: none"></div>
          <div class="results__group-2">
            <div class="results results--products">
              <h3 class="h4" id="predictive-search-products">
                Products <span style="color: #999; font-size: 12px; font-weight: normal; margin-left: 8px;">- Search with AI</span>
              </h3>
              <ul role="listbox" aria-labelledby="predictive-search-products">
      `;
      */

      // Limit to first 3 products for predictive search
      products.slice(0, 3).forEach(product => {
        html += renderProductListItem(product);
      });

      html += `
              </ul>
            </div>
          </div>
        </div>
      `;

      // Add "View more" button if there are more results
      if (apiResults.resultsCount > 3) {
        html += `
          <button class="results__search-btn" onclick="window.location.href='${Shopify.routes.root}search?q=${encodeURIComponent(searchTerms)}'">
            View more <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 5H12.5M12.5 5L8.5 1M12.5 5L8.5 9" stroke="currentColor"></path></svg>
          </button>
        `;
      }
    }

    container.innerHTML = html;
  }

  /**
   * Render product list item (matching Shopify's exact HTML structure)
   */
  function renderProductListItem(product) {
    const productUrl = Shopify.routes.root + `products/${product.handle}`;
    const imageUrl = product.featuredImage && product.featuredImage.src
      ? product.featuredImage.src
      : (product.images && product.images.length > 0 ? product.images[0].src : '');

    // Match Shopify's exact list item structure
    let html = `
      <li role="option">
        <a href="${productUrl}">
          <div class="results-products__image grid__image-ratio">
    `;

    // Product image
    if (imageUrl) {
      // Add width parameter to image URL for responsive sizing
      const imageUrlWithWidth = imageUrl.includes('?')
        ? `${imageUrl}&width=240`
        : `${imageUrl}?width=240`;

      html += `
              <img src="${imageUrlWithWidth}"
                   alt="${escapeHtml(product.title)}"
                   width="240"
                   loading="lazy"
                   sizes="100vw">
      `;
    }

    html += `
          </div>
          <div class="results-products__info">
            <span>${escapeHtml(product.title)}</span>
          </div>
        </a>
      </li>
    `;

    return html;
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Test data for development
   */
  function getTestData(query) {
    return Promise.resolve({
      "performed": true,
      "resultsCount": 2,
      "terms": query,
      "results": [
        {
          "title": "Test Product 1",
          "handle": "test-product-1",
          "price": "29.90",
          "featuredImage": {
            "src": "https://via.placeholder.com/150"
          }
        },
        {
          "title": "Test Product 2",
          "handle": "test-product-2",
          "price": "39.90",
          "featuredImage": {
            "src": "https://via.placeholder.com/150"
          }
        }
      ]
    });
  }

  console.log('[CustomPredictiveSearch] Initialized');
})();
